import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  MAX_UPLOAD_SIZE_LABEL,
  formatFileSize,
  isCompressiblePdf,
} from "@/utils/uploadSizeLimit";

interface OversizeFileModalProps {
  file: File | null;
  onReduce: () => void;
  onCancel: () => void;
  isReducing?: boolean;
}

/**
 * Shown when a picked image/PDF is over the 5MB cap — offers to reduce it
 * below the limit and upload. Non-reducible types never reach this modal;
 * they're rejected with a toast instead.
 */
export default function OversizeFileModal({
  file,
  onReduce,
  onCancel,
  isReducing = false,
}: OversizeFileModalProps) {
  if (!file) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && !isReducing && onCancel()}>
      <DialogContent className="sm:max-w-[500px] rounded-xl">
        <DialogHeader>
          <p className="text-base font-semibold text-[#051912]">
            File exceeds {MAX_UPLOAD_SIZE_LABEL}
          </p>
          <Separator />
          <DialogDescription className="text-black-7 pt-2" asChild>
            <div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
                <AlertTriangle
                  size={18}
                  className="text-amber-600 shrink-0 mt-0.5"
                />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">
                    Only files up to {MAX_UPLOAD_SIZE_LABEL} are supported
                  </p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    <span className="font-semibold">{file.name}</span> is{" "}
                    {formatFileSize(file.size)}. Do you want to reduce it to
                    under {MAX_UPLOAD_SIZE_LABEL} and upload?
                    {isCompressiblePdf(file) &&
                      " The PDF will be converted to an image-based copy — text won't be selectable."}
                  </p>
                </div>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="mt-6 flex justify-end gap-2">
          <Button variant="cancel" onClick={onCancel} disabled={isReducing}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onReduce} disabled={isReducing}>
            {isReducing ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Reducing…
              </>
            ) : (
              "Reduce & upload"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
